let dynamicByUserId: Record<number, string> = {}
let dynamicBySegment: Record<string, string> = {}
// Default WEBSITE PID per CJ publisher account, synced from `/affiliate-networks/cj-surface-pids`
// (which the backend computes with the same per-account resolution used to bake brand links).
let dynamicWebsitePidByAccount: Record<string, string> = {}
// The CURRENT authenticated user's OWN dedicated PID per account (never other users'), from the same
// endpoint's `user` map. Takes precedence over the segment default. Empty when the user has none.
let dynamicUserPidByAccount: Record<string, string> = {}

/** Default segment to fall back to when a user has no dedicated PID. */
export const DEFAULT_CJ_WEBSITE_SEGMENT = { website: '7127090' }

/** Per-account fallback WEBSITE PID when the backend map hasn't loaded (mirrors the backend bootstraps). */
export const DEFAULT_CJ_WEBSITE_PID_BY_ACCOUNT: Record<string, string> = {
  '1': '7127090',
  '2': '101850406',
}

/**
 * CJ publisher accounts whose brands the current viewer may see/act on, from `/cj-surface-pids`.
 * Defaults to BOTH accounts, which is exactly the anonymous rule — `UserCjPidsSync` only fetches when
 * authenticated, so an unfetched registry is correctly the logged-out state.
 */
let allowedCjAccounts: number[] = [1, 2]
/** Account an anonymous viewer collapses a duplicated advertiser to. Assumes 1 until fetched. */
let defaultCjWebsiteAccount = 1

/** `null`/`undefined`/`1` → account 1; `2` → account 2 (matches `brands.network_account`). */
const accountKey = (networkAccount?: number | null): string =>
  Number(networkAccount) === 2 ? '2' : '1'

/** Numeric account id for a brand's nullable `networkAccount` (null = account 1). */
const accountId = (networkAccount?: number | null): number =>
  Number(networkAccount) === 2 ? 2 : 1

/** Store the backend's per-account default WEBSITE PID map (`{ '1': pid, '2': pid }`). */
export const setCjWebsitePidsByAccount = (
  map: Record<string, string>
): void => {
  dynamicWebsitePidByAccount = { ...map }
}

/** Store the current user's own dedicated PID per account (`user` map from `/cj-surface-pids`). */
export const setCjUserPidsByAccount = (map: Record<string, string>): void => {
  dynamicUserPidByAccount = { ...map }
}

/**
 * Store CJ brand access for the current viewer, from `/cj-surface-pids`. The backend owns the rule
 * (anonymous → both accounts; own dedicated PID → that PID's account; otherwise → the default WEBSITE
 * segment's account) so web and extension can't drift from it or from each other.
 */
export const setCjBrandAccess = (params: {
  allowedAccounts?: number[] | null
  defaultWebsiteAccount?: number | null
}): void => {
  const accounts = (params.allowedAccounts ?? []).filter(
    n => n === 1 || n === 2
  )
  allowedCjAccounts = accounts.length ? accounts : [1, 2]
  defaultCjWebsiteAccount = Number(params.defaultWebsiteAccount) === 2 ? 2 : 1
}

/** Whether a brand is visible to the current viewer. Non-CJ brands are never account-gated. */
export const isCjBrandAccessible = (brand?: {
  network?: string | null
  networkAccount?: number | null
}): boolean => {
  if (!brand || brand.network !== 'commissionJunction') return true
  return allowedCjAccounts.includes(accountId(brand.networkAccount))
}

/** Bare registrable-ish host for grouping the two accounts' copies of one advertiser. */
const brandHostKey = (url?: string | null): string =>
  String(url || '')
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split('/')[0]
    .toLowerCase()

/**
 * Hide CJ brands the viewer can't access, and — only when BOTH accounts are visible (the anonymous
 * case) — collapse an advertiser that exists in both CJ accounts down to one card, preferring the
 * default WEBSITE segment's account. Without this, `/brands/`, search and category pages would show
 * the same store twice, since the two accounts' copies are genuinely separate `brands` rows (that's
 * why account 2 carries the `CJ2_` brand_id prefix). Non-CJ brands pass through untouched.
 */
export const filterBrandsByCjAccess = <
  T extends {
    network?: string | null
    networkAccount?: number | null
    url?: string | null
  }
>(
  brands: T[]
): T[] => {
  if (!Array.isArray(brands)) return brands
  const visible = brands.filter(isCjBrandAccessible)
  if (allowedCjAccounts.length < 2) return visible

  // Pass 1: per advertiser host, choose the default account's copy when present, else the first seen.
  const chosenByHost: Record<string, T> = {}
  visible.forEach(brand => {
    if (brand?.network !== 'commissionJunction') return
    const host = brandHostKey(brand.url)
    if (!host) return
    const current = chosenByHost[host]
    const isPreferred =
      accountId(brand.networkAccount) === defaultCjWebsiteAccount
    if (
      !current ||
      (isPreferred &&
        accountId(current.networkAccount) !== defaultCjWebsiteAccount)
    ) {
      chosenByHost[host] = brand
    }
  })

  // Pass 2: drop the non-chosen copies, preserving the original ordering.
  return visible.filter(brand => {
    if (brand?.network !== 'commissionJunction') return true
    const host = brandHostKey(brand.url)
    if (!host) return true
    return chosenByHost[host] === brand
  })
}

/** Keeps non-React code (e.g. `generateTrackingLink`) aligned with context after API fetch. */
export const setCjPidsRegistry = (params: {
  byUserId: Record<number, string>
  bySegment: Record<string, string>
}) => {
  dynamicByUserId = { ...params.byUserId }
  dynamicBySegment = { ...params.bySegment }
}

export const clearCjPidsRegistry = () => {
  dynamicByUserId = {}
  dynamicBySegment = {}
  dynamicWebsitePidByAccount = {}
  dynamicUserPidByAccount = {}
  // Back to the anonymous rule (both accounts) — never leave a previous user's narrower access in
  // place, which would keep brands hidden after logout.
  allowedCjAccounts = [1, 2]
  defaultCjWebsiteAccount = 1
}

export const getCommissionJunctionPidForUserId = (
  userId: number
): string | undefined =>
  dynamicByUserId[userId] ?? DEFAULT_CJ_WEBSITE_SEGMENT.website

export const getCommissionJunctionPidForSegment = (
  segment: string
): string | undefined => dynamicBySegment[segment]

/**
 * Client-side CJ deep-link PID resolution, per publisher account (`null`/`1` → account 1, `2` →
 * account 2), with precedence: the current user's OWN dedicated PID for that account, else the default
 * WEBSITE PID for that account (fallback `{1:7127090, 2:101850406}`). Account-scoped so an account-2
 * brand never gets an account-1 PID (which would break CJ attribution). Only the current user's own
 * PID is ever in `dynamicUserPidByAccount` (backend scopes the `user` map to the authed user), so no
 * other user's assignment is exposed. `userId` is accepted for signature stability but unused — the
 * `user` map is already the current user's, keyed by account.
 */
export const resolveCommissionJunctionPid = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  userId: number | undefined,
  networkAccount?: number | null
): string | undefined => {
  const key = accountKey(networkAccount)
  return (
    dynamicUserPidByAccount[key] ??
    dynamicWebsitePidByAccount[key] ??
    DEFAULT_CJ_WEBSITE_PID_BY_ACCOUNT[key]
  )
}
